import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { type QueryExecution, QueryMetricsService } from "@/services/QueryMetricsService";

const QUERY_COLORS: Record<string, string> = {
  "DriverView:driverRosterQuery": "#4caf50",
  "DriverView:driverVolatileQuery": "#8bc34a",
  "DriverView:driverUnreadCount": "#cddc39",
  "DriverView:fetchFeedCommandAliasesQuery": "#00bcd4",
  "DriverView:groupsQuery": "#009688",
  "FeedContextProvider:getAllFeedContent": "#f44336",
  "UnreadsContextProvider:unreadFeedIdsQuery": "#ff9800",
  "LeftSidebar:driverUnreadCount": "#ff5722",
  "LeftSidebar:channelUnreadCount": "#e91e63",
  "ToggleSecondWindowButton.regularChannelList": "#9c27b0",
  "CurrentFeedProvider:currentFeedQuery": "#2196f3",
  "CurrentFeedProvider:myFeedPermissionsQuery": "#03a9f4",
  "CurrentFeedProvider:currentFeedPendingInvitesSelect": "#00bcd4",
};

function getColor(queryName: string): string {
  if (QUERY_COLORS[queryName]) return QUERY_COLORS[queryName];
  let hash = 0;
  for (let i = 0; i < queryName.length; i++) {
    hash = queryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

function formatMs(ms: number): string {
  return ms < 1 ? `${(ms * 1000).toFixed(0)}us` : ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function shortenName(name: string): string {
  return name.length > 40 ? `${name.substring(0, 37)}...` : name;
}

export default function QueryTimelineModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [executions, setExecutions] = useState<QueryExecution[]>([]);
  const [windowSeconds, setWindowSeconds] = useState(10);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const load = () => {
      const cutoff = Date.now() - windowSeconds * 1000;
      setExecutions(QueryMetricsService.getExecutions({ startTime: cutoff }));
    };

    load();
    const unsubscribe = QueryMetricsService.subscribe(load);
    const interval = setInterval(load, 500);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [open, windowSeconds]);

  if (executions.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Query Timeline</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
            No query executions recorded. Enable debug mode (?debug=true) and interact with the app.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const minTimestamp = Math.min(...executions.map((e) => e.timestamp));
  const maxTimestamp = Math.max(...executions.map((e) => e.timestamp + e.duration));
  const totalSpan = maxTimestamp - minTimestamp || 1;
  const pxPerMs = 800 / totalSpan;

  const grouped = new Map<string, QueryExecution[]>();
  for (const exec of executions) {
    const existing = grouped.get(exec.queryName) ?? [];
    existing.push(exec);
    grouped.set(exec.queryName, existing);
  }

  const sortedGroups = [...grouped.entries()].sort((a, b) => {
    const aFirst = Math.min(...a[1].map((e) => e.timestamp));
    const bFirst = Math.min(...b[1].map((e) => e.timestamp));
    return aFirst - bFirst;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Query Timeline</span>
        <Box sx={{ display: "flex", gap: 1 }}>
          {[5, 10, 30, 60].map((s) => (
            <Button
              key={s}
              size="small"
              variant={windowSeconds === s ? "contained" : "outlined"}
              onClick={() => setWindowSeconds(s)}
              sx={{ minWidth: "40px", fontSize: "10px" }}
            >
              {s}s
            </Button>
          ))}
        </Box>
      </DialogTitle>
      <DialogContent ref={containerRef}>
        <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#999" }}>
          <span>0ms</span>
          <span>{formatMs(totalSpan)}</span>
        </Box>

        <Box sx={{ position: "relative", minHeight: sortedGroups.length * 28 + 20 }}>
          {sortedGroups.map(([queryName, execs], groupIndex) => (
            <Box key={queryName} sx={{ display: "flex", alignItems: "center", height: 24, mb: "4px" }}>
              <Box
                sx={{
                  width: 200,
                  flexShrink: 0,
                  fontSize: "9px",
                  fontFamily: "monospace",
                  color: getColor(queryName),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  pr: 1,
                }}
                title={queryName}
              >
                {shortenName(queryName)}
              </Box>
              <Box sx={{ position: "relative", flexGrow: 1, height: "100%" }}>
                {execs.map((exec) => {
                  const left = (exec.timestamp - minTimestamp) * pxPerMs;
                  const width = Math.max(exec.duration * pxPerMs, 2);
                  return (
                    <Box
                      key={`${exec.timestamp}-${exec.duration}`}
                      title={`${queryName}\n${formatMs(exec.duration)}\ntriggered by: ${exec.triggeringTable ?? "initial"}`}
                      sx={{
                        position: "absolute",
                        left: `${left}px`,
                        width: `${width}px`,
                        height: 16,
                        top: 4,
                        backgroundColor: getColor(queryName),
                        opacity: 0.8,
                        borderRadius: "2px",
                        cursor: "default",
                        "&:hover": { opacity: 1 },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2, borderTop: "1px solid #333", pt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {executions.length} executions across {sortedGroups.length} queries in {formatMs(totalSpan)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => QueryMetricsService.clearMetrics()} color="error" size="small">
          Clear
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
